import { Box, createStyles, Flex } from '@mantine/core';
import { formatNumber } from '@repo/common/utils/numberUtils';
import { Text, Title } from '@repo/foundations';
import { memo } from 'react';
import { formatCurrency } from '../../../utils/numberUtils';

const useStyles = createStyles(
	(theme, { noPointer }: { noPointer?: boolean }) => ({
		card: {
			gap: theme.spacing['5xs'],
			height: '72px',
			width: '100%',
			display: 'flex',
			borderRadius: theme.other.borderRadius.md,
			backgroundColor: theme.other.getColor('surface/primary/default'),
			border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
			cursor: noPointer ? 'default' : 'pointer',
			pointerEvents: noPointer ? 'none' : 'auto',
			'&:hover, &:focus': {
				backgroundColor: theme.other.getColor('surface/primary/hover'),
			},
			'&:active': {
				backgroundColor: theme.other.getColor('surface/primary/active'),
			},
		},
		title: {
			marginLeft: theme.spacing['3xs'],
		},
	})
);

interface MetricsOverviewCardProps {
	title: string;
	value: number | string;
	icon?: React.ReactNode;
	onClick?: () => void;
	noPointer?: boolean;
	format?: 'number' | 'currency';
	tooltip?: React.ReactNode;
	badge?: React.ReactNode;
}

export const MetricsOverviewCard = memo(
	({
		title,
		value,
		icon,
		onClick,
		noPointer,
		format = 'number',
		tooltip,
		badge,
	}: MetricsOverviewCardProps) => {
		const { classes } = useStyles({ noPointer });

		const formattedValue =
			typeof value === 'number'
				? format === 'currency'
					? formatCurrency(value)
					: formatNumber(value)
				: value;

		return (
			<Box w="100%" p="sm" className={classes.card} onClick={onClick}>
				<Flex direction="column" w="100%">
					<Flex align="center" gap="2xs">
						{icon && icon}
						<Text size="sm" color="text/secondary/default" weight="bold">
							{title}
						</Text>
					</Flex>
					<Flex align="center" gap="xs">
						<Title
							className={classes.title}
							size="md"
							color="text/primary/default"
						>
							{formattedValue}
						</Title>
						{badge && badge}
					</Flex>
				</Flex>
				{tooltip && tooltip}
			</Box>
		);
	}
);
